import { APP_INITIALIZER, Inject, Injectable, Provider, Renderer2, RendererFactory2 } from '@angular/core';
import { WINDOW } from '@platform-frontend/util';
import { BehaviorSubject, Observable, defer, fromEvent, fromEventPattern, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DeviceFingerprintService {
  private renderer: Renderer2;
  private scriptLoaded = false;
  private deviceFingerprintSubject$ = new BehaviorSubject<string | null>(null);
  deviceFingerprintId$ = this.deviceFingerprintSubject$.asObservable();

  constructor(rendererFactory: RendererFactory2, @Inject(WINDOW) private window: Window & { dfSet?: (field: string) => void }) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  init(): void {
    this.deviceFingerprintId$ = this.loadAdyenFingerprintScript().pipe(
      switchMap(() => this.initiateFingerprint()),
      tap(fingerprint => this.deviceFingerprintSubject$.next(fingerprint)),
      take(1)
    );
  }

  private createHiddenFingerprintField(): void {
    if (!this.window.document.getElementById('deviceFingerprintField')) {
      const input = this.renderer.createElement('input');
      input.type = 'hidden';
      input.id = 'deviceFingerprintField';
      input.name = 'dfValue';
      this.renderer.appendChild(this.window.document.body, input);
    }
  }

  private loadAdyenFingerprintScript(): Observable<void> {
    return defer(() => {
      if (this.scriptLoaded || document.getElementById('adyen-fingerprint-script')) {
        return of(undefined);
      }

      const script = this.renderer.createElement('script');
      script.id = 'adyen-fingerprint-script';
      script.type = 'text/javascript';
      script.src = `https://live.adyen.com/hpp/js/df.js?v=${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;
      script.async = true;

      this.renderer.appendChild(document.body, script);

      return fromEvent(script, 'load').pipe(
        tap(() => {
          this.scriptLoaded = true;
        }),
        map(() => undefined),
        take(1)
      );
    });
  }

  private waitForFingerprint(): Observable<string> {
    return defer(() => {
      const fingerprintField = document.getElementById('deviceFingerprintField') as HTMLInputElement;

      if (fingerprintField?.value) {
        return of(fingerprintField.value);
      }

      return fromEventPattern<string>(
        handler => {
          const observer = new MutationObserver(() => {
            const fingerprint = fingerprintField?.value;
            if (fingerprint) {
              handler(fingerprint);
            }
          });

          observer.observe(document, { attributes: true, childList: true, subtree: true });
          return observer;
        },
        (_, observer) => observer.disconnect()
      ).pipe(take(1));
    });
  }

  private initiateFingerprint(): Observable<string> {
    return defer(() => {
      this.createHiddenFingerprintField();
      if (this.window.dfSet) {
        this.window.dfSet('deviceFingerprintField');
      }
      return this.waitForFingerprint();
    });
  }
}

export const DeviceFingerprintServiceInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (deviceFingerprintService: DeviceFingerprintService) => () => deviceFingerprintService.init(),
  deps: [DeviceFingerprintService],
  multi: true
};
